import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { FetchEventSourceInit } from '@microsoft/fetch-event-source';
import { supabase } from '../../lib/supabase';


/**
 * Industrial-Grade SSE Transport Wrapper for ORION
 * 
 * Features:
 * - Automatic Supabase Auth injection
 * - Resilient buffering for Vercel Edge / CDNs
 * - X-Accel-Buffering: no for bypass of proxy cache
 * - POST-method based streaming for large payloads
 */

export interface StreamOptions extends Omit<FetchEventSourceInit, 'headers' | 'method'> {
    headers?: Record<string, string>;
    onChunk?: (data: string) => void;
}

export const createAuthenticatedStream = async (
    url: string,
    body: Record<string, unknown>,
    options: StreamOptions
) => {
    // 1. Get stable auth session
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
        throw new Error('Authentication required for secure streaming');
    }

    // 2. Configure Industrial Headers
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
        'X-Accel-Buffering': 'no', // Critical for Vercel/Nginx bypass
        ...options.headers
    };

    // 3. Native AbortController for lifecycle management
    const ctrl = options.signal ? null : new AbortController();
    const signal = options.signal || ctrl?.signal;

    try {
        await fetchEventSource(url, {
            ...options,
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal,
            async onopen(response) {
                if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
                    return; // Success
                } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                    throw new Error(`Streaming failed: HTTP ${response.status} ${response.statusText}`);
                }
            },
            onmessage(ev) {
                if (options.onChunk) {
                    options.onChunk(ev.data);
                } else if (options.onmessage) {
                    options.onmessage(ev);
                }
            },
            onerror(err) {
                console.error('[SSE-Transport] Critical Stream Error:', err);
                if (options.onerror) options.onerror(err);
                throw err;
            },
            onclose() {
                if (options.onclose) options.onclose();
            }
        });
    } catch (err) {
        console.error('[SSE-Transport] Fatal connection failure:', err);
        throw err;
    }
};
